import _orderBy from 'lodash/orderBy'
import { IAgency } from '@/shared/models/agency'
import { IBrand } from '@/shared/models/brand'

/** Mirrors alc-pag-server/repositories/ClientsRepo mapFieldNameToTable — fields enriched for uploadPortalSlug fetches only. */
const HYDRATABLE_UPLOAD_PORTAL_FIELD_KEYS = new Set([
  'brandId',
  'agencyId',
  'projectTypeId',
  'bidTypeId',
  'projectCategoryId',
  'projectRegionId',
  'projectStatusId',
  'campaignId',
  'marketId',
  'agencyProducerId',
])

export type UploadPortalSelectHydration = {
  agencies?: { id: number; name: string }[]
  projectTypes?: { id: number; projectType: string }[]
  bidTypes?: { id: number; bidType: string }[]
  projectCategories?: { id: number; category: string }[]
  projectRegions?: { id: number; region: string }[]
  projectStatuses?: { id: number; status: string }[]
  campaigns?: { id: number; name: string }[]
  markets?: { id: number; name: string }[]
  agencyProducers?: { id: number; name: string; clientId: number }[]
}

export function getUploadPortalSelectFieldsToHydrate(c: any): Set<string> {
  const out = new Set<string>()
  const visit = (arr: any[]) => {
    for (const f of arr || []) {
      if (!f?.field || !HYDRATABLE_UPLOAD_PORTAL_FIELD_KEYS.has(f.field)) continue
      const hasOptions = Array.isArray(f.options) && f.options.length > 0
      if (!hasOptions) out.add(f.field)
    }
  }
  visit(c?.uploadPortalFields?.required || [])
  visit(c?.uploadPortalFields?.optional || [])
  return out
}

export function injectUploadPortalSelectOptions(c: any, lists: UploadPortalSelectHydration) {
  if (!c?.uploadPortalFields) return c
  const clientId = c.id

  const fill = (field: any) => {
    const hasOptions = Array.isArray(field.options) && field.options.length > 0
    if (hasOptions) return field

    if (field.field === 'brandId' && c.brands?.length) {
      return {
        ...field,
        options: c.brands.map((b: IBrand) => ({ id: b.id, name: b.name })),
      }
    }
    if (field.field === 'agencyId' && lists.agencies?.length) {
      return {
        ...field,
        options: lists.agencies.map((a) => ({ id: a.id, name: a.name })),
      }
    }
    if (field.field === 'projectTypeId' && lists.projectTypes?.length) {
      return {
        ...field,
        options: lists.projectTypes.map((t) => ({ id: t.id, name: t.projectType })),
      }
    }
    if (field.field === 'bidTypeId' && lists.bidTypes?.length) {
      return {
        ...field,
        options: lists.bidTypes.map((t) => ({ id: t.id, name: t.bidType })),
      }
    }
    if (field.field === 'projectCategoryId' && lists.projectCategories?.length) {
      return {
        ...field,
        options: lists.projectCategories.map((t) => ({ id: t.id, name: t.category })),
      }
    }
    if (field.field === 'projectRegionId' && lists.projectRegions?.length) {
      return {
        ...field,
        options: lists.projectRegions.map((t) => ({ id: t.id, name: t.region })),
      }
    }
    if (field.field === 'projectStatusId' && lists.projectStatuses?.length) {
      return {
        ...field,
        options: lists.projectStatuses.map((t) => ({ id: t.id, name: t.status })),
      }
    }
    if (field.field === 'campaignId' && lists.campaigns?.length) {
      return {
        ...field,
        options: lists.campaigns.map((t) => ({ id: t.id, name: t.name })),
      }
    }
    if (field.field === 'marketId' && lists.markets?.length) {
      return {
        ...field,
        options: lists.markets.map((t) => ({ id: t.id, name: t.name })),
      }
    }
    if (field.field === 'agencyProducerId' && lists.agencyProducers?.length) {
      const forClient = lists.agencyProducers.filter((p) => p.clientId === clientId)
      if (forClient.length) {
        return {
          ...field,
          options: forClient.map((p) => ({ id: p.id, name: p.name })),
        }
      }
    }
    return field
  }

  return {
    ...c,
    uploadPortalFields: {
      ...c.uploadPortalFields,
      required: (c.uploadPortalFields.required || []).map(fill),
      optional: (c.uploadPortalFields.optional || []).map(fill),
    },
  }
}

/** Sort helpers aligned with existing widget queries */
export function orderAgencies(agencies: any[]) {
  return _orderBy(agencies || [], ['name'])
}

export function orderProjectTypes(rows: any[]) {
  return _orderBy(rows || [], ['projectType'])
}

export function orderBidTypes(rows: any[]) {
  return _orderBy(rows || [], ['bidType'])
}

export function orderProjectCategories(rows: any[]) {
  return _orderBy(rows || [], ['category'])
}

export function orderProjectRegions(rows: any[]) {
  return _orderBy(rows || [], ['region'])
}

export function orderProjectStatuses(rows: any[]) {
  return _orderBy(rows || [], ['status'])
}

export function orderCampaigns(rows: any[]) {
  return _orderBy(rows || [], ['name'])
}

export function orderMarkets(rows: any[]) {
  return _orderBy(rows || [], ['name'])
}

export function orderAgencyProducers(rows: any[]) {
  return _orderBy(rows || [], ['name'])
}
